/**
 * Settings Service
 * Handles user settings and preferences with persistence
 */

import api from './api';

// Local storage keys
const STORAGE_KEYS = {
  THEME: 'mq_admin_theme',
  NOTIFICATIONS: 'mq_admin_notifications',
};

class SettingsService {
  /**
   * Get user profile
   */
  async getProfile() {
    const response = await api.get('/auth/profile');

    if (response.success && response.data) {
      return response.data.user || response.data;
    }

    throw new Error('Failed to fetch profile');
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   */
  async updateProfile(profileData) {
    const response = await api.put('/auth/profile', profileData);

    if (response.success) {
      return response.data;
    }

    throw new Error(response.message || 'Failed to update profile');
  }

  /**
   * Upload avatar
   * @param {File} file - Avatar image file
   */
  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await api.post('/auth/avatar', formData);

    if (response.success) {
      return response.data;
    }

    throw new Error('Failed to upload avatar');
  }

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });

    if (response.success) {
      return true;
    }

    throw new Error(response.message || 'Failed to change password');
  }

  /**
   * Get theme preference (from localStorage)
   */
  getTheme() {
    return localStorage.getItem(STORAGE_KEYS.THEME) || 'light';
  }

  /**
   * Set theme preference
   * @param {string} theme - 'light' or 'dark'
   */
  setTheme(theme) {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
  }

  /**
   * Get notification preferences (from localStorage)
   */
  getNotificationPreferences() {
    const stored = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return { push: false, dnd: false };
      }
    }
    return { push: false, dnd: false };
  }

  /**
   * Set notification preferences
   * @param {Object} prefs - { push: boolean, dnd: boolean }
   */
  setNotificationPreferences(prefs) {
    localStorage.setItem(STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(prefs));
  }

  /**
   * Get all settings
   */
  async getAllSettings() {
    try {
      const profile = await this.getProfile();
      const theme = this.getTheme();
      const notifications = this.getNotificationPreferences();

      return {
        profile,
        theme,
        notifications,
      };
    } catch (err) {
      console.error('Failed to load settings:', err);
      throw err;
    }
  }

  /**
   * Get company settings (admin only)
   */
  async getCompanySettings() {
    const response = await api.get('/settings/company');

    if (response.success && response.data) {
      return response.data;
    }

    // Return default settings if endpoint doesn't exist
    return {
      name: 'Marquee Couriers',
      email: 'support@marqueecouriers.com',
      phone: '',
      address: '',
      timezone: 'America/New_York',
      currency: 'USD',
    };
  }

  /**
   * Update company settings (admin only)
   * @param {Object} settings - Company settings to update
   */
  async updateCompanySettings(settings) {
    const response = await api.put('/settings/company', settings);

    if (response.success) {
      return response.data;
    }

    throw new Error('Failed to update company settings');
  }
}

const settingsService = new SettingsService();
export default settingsService;
